import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Calendar, User, Clock, ArrowLeft, Share2, Heart, Bookmark } from "lucide-react";
import ArticleImageSlider from "./ArticleImageSlider";

const ArticlePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [likedArticles, setLikedArticles] = useState(new Set());
  const [bookmarkedArticles, setBookmarkedArticles] = useState(new Set());

  // Sample articles data with multiple images and enhanced content
  const articles = [
    {
      id: 1,
      title: "Community Business Expo 2024",
      excerpt: "Join us for the biggest business networking event of the year, featuring over 100 local businesses and entrepreneurs.",
      content: `We are thrilled to announce the upcoming Community Business Expo 2024, the premier networking event for local businesses and entrepreneurs. This year's expo promises to be our biggest yet, with over 100 participating businesses from various sectors.

## What to Expect

The event will feature an impressive lineup of activities and opportunities designed to foster growth, collaboration, and innovation within our business community.

### Key Features

- **Live Product Demonstrations**: See cutting-edge products and services in action
- **Networking Sessions**: Connect with industry leaders and potential partners
- **Interactive Workshops**: Learn from experts about business growth strategies
- **Panel Discussions**: Gain insights from successful entrepreneurs and business veterans
- **Exhibition Spaces**: Showcase your products and services to a targeted audience
- **B2B Matchmaking**: Our intelligent system will connect you with the most relevant business contacts

### Schedule Highlights

**Morning Session (9:00 AM - 12:00 PM)**
- Opening ceremony and keynote speech
- Product showcase exhibitions
- Business matchmaking sessions

**Afternoon Session (1:00 PM - 5:00 PM)**
- Industry-specific panel discussions
- Interactive workshops and training sessions
- Networking lunch and coffee breaks

**Evening Session (5:00 PM - 7:00 PM)**
- Awards ceremony for outstanding businesses
- Closing networking reception

### Who Should Attend

Whether you're a startup founder looking for investors, a small business owner seeking growth opportunities, or an established entrepreneur wanting to expand your network, this expo offers unparalleled opportunities to connect, learn, and grow.

### Registration Details

Early bird registration is now open with special discounts for members. Limited exhibition spaces are available, so we encourage you to secure your spot today.

Mark your calendars and prepare for an unforgettable experience that could transform your business journey. This is more than just an event – it's a catalyst for your business success.`,
      author: "Admin Team",
      date: "2024-02-01",
      readTime: "5 min read",
      category: "Events",
      featured: true,
      images: [
        "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=400&fit=crop",
        "https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=800&h=400&fit=crop",
        "https://images.unsplash.com/photo-1497366216548-375f70e65555?w=800&h=400&fit=crop"
      ]
    },
    {
      id: 2,
      title: "New Digital Services Available",
      excerpt: "We're excited to announce the launch of our new digital marketing and web development services for member businesses.",
      content: `In response to the growing digital needs of our business community, we are proud to launch our comprehensive digital services suite. These services are designed to help member businesses establish and strengthen their online presence in today's competitive digital landscape.

## Our Digital Transformation Journey

The business world has undergone a massive digital transformation, and we're committed to ensuring our members stay ahead of the curve. Our new digital services are built on cutting-edge technology and industry best practices.

### Comprehensive Service Offerings

**Web Development Solutions**
- Custom website development tailored to your brand
- E-commerce platforms for online sales
- Mobile-responsive design for all devices
- Content management systems for easy updates
- Website optimization for speed and performance

**Digital Marketing Services**
- Search Engine Optimization (SEO) to improve visibility
- Social media marketing management across all platforms
- Email marketing campaigns with advanced analytics
- Pay-per-click advertising for immediate results
- Content marketing strategies to engage your audience

**Advanced Analytics & Reporting**
- Website traffic analysis and user behavior insights
- Conversion rate optimization
- Social media performance metrics
- ROI tracking for all digital campaigns
- Custom dashboard for real-time data monitoring

### Why Choose Our Digital Services?

Our team of experienced digital experts brings years of industry knowledge and technical expertise. We understand that every business is unique, and we're committed to delivering solutions that drive real, measurable results.

### Success Stories

Several of our beta testers have already seen remarkable results:
- 300% increase in website traffic for local retailers
- 150% growth in online leads for service providers
- 200% improvement in social media engagement

### Special Member Benefits

Take advantage of our special introductory pricing available exclusively for members. This includes a free digital audit of your current online presence and a customized strategy session.

Don't let your business fall behind in the digital age. Partner with us to transform your online presence and achieve your growth goals.`,
      author: "Tech Team",
      date: "2024-01-28",
      readTime: "4 min read",
      category: "Technology",
      featured: false,
      images: [
        "https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=800&h=400&fit=crop",
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=400&fit=crop",
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop"
      ]
    },
    {
      id: 3,
      title: "Member Success Stories",
      excerpt: "Discover how our members are thriving with the support of our business community and resources.",
      content: `Success stories inspire and motivate us all. This month, we're highlighting three remarkable members who have achieved significant milestones through their participation in our business community. Their journeys demonstrate the power of collaboration, perseverance, and strategic thinking.

## Sarah's Bakery: From Local Shop to Regional Success

Sarah's Bakery, a family-owned business started in a small kitchen, has experienced phenomenal growth over the past year. After implementing our digital marketing strategies and participating in our business development workshops, Sarah saw a **300% increase in online orders** and expanded her team from 3 to 12 employees.

### The Challenge
Sarah struggled with limited local reach and inefficient order management. Her delicious products deserved a wider audience, but she lacked the technical expertise to scale her operations.

### The Solution
Through our community programs, Sarah received:
- Professional website development with integrated ordering system
- Social media marketing training and content strategy
- Business management workshops
- Networking opportunities with suppliers and distributors

### The Results
Today, Sarah's Bakery ships products across three states and has been featured in several food magazines. "The support and guidance I received were invaluable," says Sarah. "I couldn't have achieved this growth without the community's help."

## TechStart Solutions: Connecting with Fortune 500 Clients

TechStart Solutions, a local IT consulting firm founded by Michael Chen, secured five major corporate clients through networking events organized by our community. This represents a **400% increase in revenue** over just six months.

### Breaking Through the Barriers
Like many small tech firms, TechStart struggled to break into the enterprise market. They had the technical expertise but lacked the connections and credibility to attract large clients.

### Strategic Networking
Our community facilitated:
- Introduction to key decision makers at major corporations
- Opportunities to showcase their expertise at industry events
- Mentorship from established IT entrepreneurs
- Access to bidding opportunities for large projects

### Transformation
"The connections I made here transformed my business," shares founder Michael. "We went from chasing small projects to competing for multi-million dollar contracts."

## GreenThumb Landscaping: Sustainable Growth Through Innovation

GreenThumb Landscaping, owned by Jennifer Martinez, expanded their team and service area by **150%** after participating in our business growth workshops. They also pioneered eco-friendly landscaping practices that set them apart from competitors.

### Embracing Sustainability
Jennifer recognized the growing demand for environmentally conscious services but needed guidance on how to implement sustainable practices profitably.

### Community Support
Through our programs, Jennifer accessed:
- Training in sustainable landscaping techniques
- Marketing support to promote eco-friendly services
- Business planning workshops for expansion strategies
- Peer mentoring from other service business owners

### Market Leadership
"The practical knowledge and peer support were game-changers," notes Jennifer. "We're now the go-to landscaping service for environmentally conscious clients."

## The Power of Community

These success stories demonstrate the transformative power of community collaboration. Each business leveraged different aspects of our support network, but all share common themes:

- **Willingness to Learn**: Successful members actively participate in workshops and training
- **Strategic Networking**: They build meaningful relationships, not just collect contacts
- **Implementation**: They apply what they learn and take action on opportunities
- **Giving Back**: Many successful members become mentors themselves

## Your Success Story Starts Here

Whether you're just starting out or looking to scale your existing business, our community provides the resources, connections, and support you need to succeed. Your success story could be next!`,
      author: "Community Manager",
      date: "2024-01-25",
      readTime: "6 min read",
      category: "Success Stories",
      featured: false,
      images: [
        "https://images.unsplash.com/photo-1497366216548-375f70e65555?w=800&h=400&fit=crop",
        "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=400&fit=crop",
        "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=400&fit=crop"
      ]
    }
  ];

  useEffect(() => {
    // Find the article by ID
    const foundArticle = articles.find(a => a.id === parseInt(id));
    setArticle(foundArticle);

    // Load saved states from localStorage
    const savedLikes = localStorage.getItem('likedArticles');
    const savedBookmarks = localStorage.getItem('bookmarkedArticles');

    if (savedLikes) setLikedArticles(new Set(JSON.parse(savedLikes)));
    if (savedBookmarks) setBookmarkedArticles(new Set(JSON.parse(savedBookmarks)));
  }, [id]);

  const handleLikeArticle = () => {
    if (!article) return;

    setLikedArticles(prev => {
      const newLikes = new Set(prev);
      if (newLikes.has(article.id)) {
        newLikes.delete(article.id);
      } else {
        newLikes.add(article.id);
      }
      localStorage.setItem('likedArticles', JSON.stringify([...newLikes]));
      return newLikes;
    });
  };

  const handleBookmarkArticle = () => {
    if (!article) return;

    setBookmarkedArticles(prev => {
      const newBookmarks = new Set(prev);
      if (newBookmarks.has(article.id)) {
        newBookmarks.delete(article.id);
      } else {
        newBookmarks.add(article.id);
      }
      localStorage.setItem('bookmarkedArticles', JSON.stringify([...newBookmarks]));
      return newBookmarks;
    });
  };

  const handleShareArticle = () => {
    if (!article) return;

    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt,
        url: window.location.href
      }).catch(err => console.log('Error sharing:', err));
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Article link copied to clipboard!');
    }
  };

  if (!article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#135879] mb-4">Article not found</h2>
          <Link
            to="/newsletter"
            className="inline-flex items-center text-[#135879] hover:text-[#0f4568] font-medium"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Newsletter
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Newsletter-style Header */}
      <header className="bg-gradient-to-r from-[#135879] to-[#1a6d91] text-white border-b border-[#d4af37]">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-end">
            <Link
              to="/newsletter"
              className="inline-flex items-center text-white/90 hover:text-white transition-colors duration-200"
            >
              <ArrowLeft size={20} className="mr-2" />
              <span className="font-medium">back</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <main className="container mx-auto px-4 py-8 max-w-5xl">


        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Hero Section with Image */}
          <ArticleImageSlider images={article.images} title={article.title} />

          <div className="px-8 md:px-20 pt-16 pb-10 text-center">
            <span className="inline-block mb-6 px-4 py-1 rounded-full bg-[#6b8f71]/10 text-[#6b8f71] text-xs tracking-widest font-semibold uppercase">
              {article.category}
            </span>

            <h1 className="text-4xl md:text-6xl font-serif font-bold text-gray-900 leading-tight max-w-4xl mx-auto">
              {article.title}
            </h1>

            <p className="mt-6 text-xl text-gray-600 max-w-2xl mx-auto">
              {article.excerpt}
            </p>

            <div className="mt-10 flex justify-center flex-wrap gap-6 text-sm text-gray-500">
              <span className="flex items-center gap-2"><User size={14} /> {article.author}</span>
              <span className="flex items-center gap-2"><Calendar size={14} /> {new Date(article.date).toLocaleDateString()}</span>
              <span className="flex items-center gap-2"><Clock size={14} /> {article.readTime}</span>
            </div>
          </div>

          {/* Article Body */}
          <div className="p-8 md:p-12">

            {/* Article Content with Multiple Images */}
            <div className="prose prose-lg max-w-none">
              <div className="text-gray-700 leading-relaxed space-y-8">
                {/* Split content and render images throughout */}
                {article.content.split('\n\n').map((paragraph, index) => {

                  return (
                    <div key={index}>
                      <div className="whitespace-pre-line text-lg leading-relaxed">
                        {paragraph}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Article Actions */}
            <div className="mt-12 pt-8 border-t-2 border-[#d4af37]/20">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleLikeArticle}
                    className={`flex items-center space-x-2 px-6 py-3 rounded-full transition-all duration-200 ${likedArticles.has(article.id)
                      ? 'bg-red-100 text-red-600 hover:bg-red-200 border-2 border-red-200'
                      : 'bg-[#135879]/10 text-[#135879] hover:bg-[#135879]/20 border-2 border-[#d4af37]/30'
                      }`}
                  >
                    <Heart size={18} className={likedArticles.has(article.id) ? 'fill-current' : ''} />
                    <span className="font-medium">
                      {likedArticles.has(article.id) ? 'Liked' : 'Like'}
                    </span>
                  </button>
                  <button
                    onClick={handleBookmarkArticle}
                    className={`flex items-center space-x-2 px-6 py-3 rounded-full transition-all duration-200 ${bookmarkedArticles.has(article.id)
                      ? 'bg-blue-100 text-blue-600 hover:bg-blue-200 border-2 border-blue-200'
                      : 'bg-[#135879]/10 text-[#135879] hover:bg-[#135879]/20 border-2 border-[#d4af37]/30'
                      }`}
                  >
                    <Bookmark size={18} className={bookmarkedArticles.has(article.id) ? 'fill-current' : ''} />
                    <span className="font-medium">
                      {bookmarkedArticles.has(article.id) ? 'Saved' : 'Save'}
                    </span>
                  </button>
                  <button
                    onClick={handleShareArticle}
                    className="flex items-center space-x-2 px-6 py-3 rounded-full bg-[#135879]/10 text-[#135879] hover:bg-[#135879]/20 border-2 border-[#d4af37]/30 transition-all duration-200"
                  >
                    <Share2 size={18} />
                    <span className="font-medium">Share</span>
                  </button>
                </div>
                <Link
                  to="/newsletter"
                  className="inline-flex items-center text-[#135879] hover:text-[#0f4568] font-medium transition-colors duration-200"
                >
                  <ArrowLeft size={18} className="mr-2" />
                  More Articles
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-[#135879] mb-8 text-center">More Articles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.filter(a => a.id !== article.id).map((relatedArticle) => (
              <Link
                key={relatedArticle.id}
                to={`/article/${relatedArticle.id}`}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-[#d4af37]/20 hover:border-[#d4af37]/40"
              >
                <div className="h-32 relative overflow-hidden">
                  <img
                    src={relatedArticle.images[0]}
                    alt={relatedArticle.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <span className="inline-block bg-[#d4af37] text-black px-3 py-1 rounded-full text-xs font-bold mb-2">
                      {relatedArticle.category}
                    </span>
                    <h4 className="text-white font-bold leading-tight text-sm">{relatedArticle.title}</h4>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-gray-600 text-sm line-clamp-2 mb-3">{relatedArticle.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{relatedArticle.readTime}</span>
                    <span className="text-[#135879] font-medium group-hover:text-[#0f4568]">
                      Read More →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ArticlePage;
